import type { FunctionComponent } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'
import type { BadgeTheme } from '@which/seatbelt'
import { Badge, Checkbox, CheckButton, TypographyV2 as Typography } from '@which/seatbelt'
import { SolidPadlockIcon } from '@which/seatbelt/src/components/Icons/Miscellaneous'
import type { FilterData } from '@which/shared'
import { dynamicDatalayerPush } from '@which/shared'

import type { Filter as FilterType } from '../../../../../generated/frontend'
import type { PageParams } from '../../../../../routes'
import type {
  FilterCallback,
  FilterItemIntersection,
} from '../../../../../shared/types/ReviewsFilters'
import { isFilterActive } from '../../../../../shared/utils/is-filter-active'
import { DropdownRange } from '../DropdownRange/DropdownRange'
import { BADGES } from '../Filters/constants/external-ids'
import { FilterTooltip } from '../Filters/FilterTooltip/FilterTooltip'
import { ShowMoreFilters } from '../ShowMoreFilters/ShowMoreFilters'
import styles from './Filter.module.scss'
import { getAriaLabel, getFilterAttributeValue } from './utils'

export const Filter: FunctionComponent<FilterProps> = ({
  filterItems,
  componentType,
  filterLabel,
  filterCallback = () => null,
  basicFilterData = {},
  externalId,
  exclusion,
  disabled,
}) => {
  const { categorySlug } = useParams<PageParams>()

  const showMoreCallback = (eventAction: string) =>
    dynamicDatalayerPush({
      eventAction,
      eventLabel: `${categorySlug} - ${filterLabel}`,
      eventCategory: 'Product Listing',
    })

  switch (componentType) {
    case 'checkbox':
      return (
        <>
          {filterItems?.map(
            ({ code, label, tooltip, valueStr, valueBool, from, to, count }, index) => {
              const filterAttributeValue = getFilterAttributeValue({
                valueBool,
                valueStr,
                from,
                to,
              })
              const path = `${externalId}.${code}`
              const isCheckboxFilterActive = isFilterActive(
                path,
                basicFilterData,
                filterAttributeValue
              )

              const handleCheckboxOnChange = () => {
                dynamicDatalayerPush({
                  eventAction: `${categorySlug} - ${filterLabel} - ${label}`,
                  eventCategory: 'refine results',
                  eventLabel: isCheckboxFilterActive ? 'false' : 'true',
                })
                filterCallback({ path, filterValue: filterAttributeValue })
              }

              const checkboxProps = {
                id: `${code}-${index}`,
                name: `${code}-${index}`,
                /* @TODO - remove this comm after making "label" an optional prop in SB Checkbox component */
                label: '',
                value: filterAttributeValue,
                checked: isCheckboxFilterActive,
                onChangeCallback: handleCheckboxOnChange,
                disabled,
                labelChildren: checkboxLabelChildren({ externalId, code, count, label, disabled }),
                ariaLabel: getAriaLabel(label, count),
              }

              return (
                ((externalId === BADGES && disabled) || !!count) && (
                  <ShowMoreFilters
                    index={index}
                    itemsLength={filterItems.length}
                    externalId={externalId}
                    key={`${externalId}-${index}`}
                    showMoreCallback={showMoreCallback}
                  >
                    <div
                      key={`${code}-${index}`}
                      className={styles.checkboxWrapper}
                      data-testid="filter-checkbox-wrapper"
                    >
                      <Checkbox className={styles.checkbox} {...checkboxProps} />
                      {tooltip && Object.keys(tooltip).length > 0 && (
                        <FilterTooltip
                          categorySlug={categorySlug}
                          filterLabel={filterLabel}
                          title={tooltip.label || ''}
                          description={tooltip.description || ''}
                        />
                      )}
                    </div>
                  </ShowMoreFilters>
                )
              )
            }
          )}
        </>
      )
    case 'checkbutton':
      /* TODO move this logic to BE */
      const filterItemsWithCount = filterItems.filter((item) => item.count !== 0)

      return (
        <>
          {filterItemsWithCount?.map(
            ({ code, label, valueBool, valueStr, to, from, options, count, tooltip }, index) => {
              const path = `${externalId}.${code}`

              if (exclusion) {
                return (
                  <ShowMoreFilters
                    index={index}
                    itemsLength={filterItems.length}
                    externalId={externalId}
                    key={`${externalId}-${index}`}
                    showMoreCallback={showMoreCallback}
                  >
                    <>
                      {label && filterItems.length > 1 && (
                        <Typography
                          className={styles.exclusionFilterTitle}
                          textStyle="sb-text-interface-body-small-regular"
                        >
                          {label}
                        </Typography>
                      )}
                      {tooltip && Object.keys(tooltip).length > 0 && (
                        <FilterTooltip
                          categorySlug={categorySlug}
                          filterLabel={filterLabel}
                          title={tooltip.label || ''}
                          description={tooltip.description || ''}
                          displayType={filterItems.length > 1 ? 'inline' : 'block'}
                        />
                      )}
                    </>
                    <div className={styles.exclusionFilterWrapper}>
                      {options?.map(
                        (
                          { label: optionsLabel, optionValueBool, optionValueStr, optionCount },
                          optionsIndex
                        ) => {
                          const optionValue = optionValueStr || optionValueBool
                          const isExclusionFilterActive = isFilterActive(
                            path,
                            basicFilterData,
                            `${optionValue}`
                          )

                          const handleExclusionCheckButtonOnChange = ({
                            value,
                          }: {
                            value?: string
                          }) => {
                            dynamicDatalayerPush({
                              eventAction: `${categorySlug} - ${filterLabel}${
                                label ? ` - ${label}` : ''
                              } - ${optionsLabel}`,
                              eventCategory: 'refine results',
                              eventLabel: isExclusionFilterActive ? 'false' : 'true',
                            })
                            filterCallback({ path, filterValue: value })
                          }

                          return (
                            <CheckButton
                              key={`${code}-${optionsIndex}`}
                              id={`${code}-${optionsIndex}`}
                              name={`${code}-${optionsIndex}`}
                              value={`${optionValue}`}
                              primaryLabel={optionsLabel || ''}
                              disabled={optionCount === 0}
                              secondaryLabel={`${optionCount}`}
                              className={styles.exclusionFilter}
                              onChangeCallback={handleExclusionCheckButtonOnChange}
                              selected={isExclusionFilterActive}
                              ariaLabel={getAriaLabel(`${label} ${optionsLabel}`, optionCount)}
                            />
                          )
                        }
                      )}
                    </div>
                  </ShowMoreFilters>
                )
              }

              const filterAttributeValue = getFilterAttributeValue({
                valueBool,
                valueStr,
                from,
                to,
              })
              const isCheckButtonFilterActive = isFilterActive(
                path,
                basicFilterData,
                filterAttributeValue
              )

              const handleCheckButtonOnChange = ({ value }: { value?: string }) => {
                dynamicDatalayerPush({
                  eventAction: `${categorySlug} - ${filterLabel} - ${label}`,
                  eventCategory: 'refine results',
                  eventLabel: isCheckButtonFilterActive ? 'false' : 'true',
                })
                filterCallback({ path, filterValue: value })
              }

              const primaryLabel = getCheckButtonPrimaryLabel({
                categorySlug,
                filterAttributeValue,
                label,
                code,
              })

              const ariaLabel =
                typeof primaryLabel === 'string' ? primaryLabel : filterAttributeValue

              return (
                <ShowMoreFilters
                  index={index}
                  itemsLength={filterItemsWithCount.length}
                  externalId={externalId}
                  key={`${externalId}-${index}`}
                  showMoreCallback={showMoreCallback}
                  code={code}
                >
                  <CheckButton
                    className={styles.nonExclusionButton}
                    key={`${code}-${index}`}
                    id={`${code}-${index}`}
                    name={`${code}-${index}`}
                    value={filterAttributeValue}
                    primaryLabel={primaryLabel}
                    secondaryLabel={`${count}`}
                    onChangeCallback={handleCheckButtonOnChange}
                    selected={isCheckButtonFilterActive}
                    ariaLabel={getAriaLabel(ariaLabel, count)}
                  />
                </ShowMoreFilters>
              )
            }
          )}
        </>
      )
    case 'dropdown':
      return (
        <DropdownRange
          {...{
            filterLabel,
            filterItems,
            externalId,
            filterCallback,
            basicFilterData,
            categorySlug,
          }}
        />
      )

    default:
      return null
  }
}

///////IMPLEMENTATION/////////

type FilterProps = {
  filterItems: FilterItemIntersection[]
  componentType: FilterType['componentType']
  filterLabel: FilterType['filterLabel']
  externalId: FilterType['externalId']
  filterCallback?: (options: FilterCallback) => void
  basicFilterData?: FilterData
  exclusion: FilterType['exclusion']
  disabled: boolean
}

type CheckboxLabelChildrenArgs = {
  externalId: string
  code?: string
  count?: number
  label?: string
  disabled?: boolean
}

type GetCheckButtonPrimaryLabelArgs = {
  categorySlug: string
  filterAttributeValue: string
  label?: string
  code?: string
}

const checkboxLabelChildren = ({
  externalId,
  code,
  count,
  label,
  disabled,
}: CheckboxLabelChildrenArgs) => {
  const isBadgesFilter = externalId === BADGES
  return (
    <span className={isBadgesFilter ? styles.iconWrapper : undefined}>
      {disabled && (
        <SolidPadlockIcon data-testid="solid-padlock" className={styles.solidPadlockIcon} />
      )}
      {isBadgesFilter ? (
        <Badge data-testid="badge" theme={code?.replace('_', ' ') as BadgeTheme} />
      ) : (
        label
      )}
      {!(isBadgesFilter && disabled) && (
        <span
          className={styles.productCount}
          data-testid={`with-${isBadgesFilter ? 'badge-and-' : ''}count`}
        >
          ({count})
        </span>
      )}
    </span>
  )
}

const getCheckButtonPrimaryLabel = ({
  categorySlug,
  filterAttributeValue,
  label,
  code,
}: GetCheckButtonPrimaryLabelArgs) =>
  code === 'manufacturer' ? (
    <a
      href={`/reviews/${categorySlug}/${filterAttributeValue.replace(/_/g, '-')}/brand`}
      className={styles.seoFriendly}
      tabIndex={-1}
    >
      {label}
    </a>
  ) : (
    label
  )
